function toNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  // Strip only currency formatting ($ , whitespace), then require the
  // whole value to be a valid number — a stray letter must reject the
  // input, not get silently rewritten into a number.
  const cleaned = v.trim().replace(/[$,\s]/g, '');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** The effective tax rate an invoice implies: tax ÷ the pre-tax base. The
 *  invoice total is tax-INCLUDED (gross), so the base is `total − tax`, and
 *  a $113 invoice showing $13 tax reads 13% (not 13/113 ≈ 11.5%). This
 *  matches the engine's `implied_tax_rate = tax / bill` convention, where
 *  `bill` is the net expense — a rate is always quoted on the pre-tax base.
 *  Null when either input is missing / unparseable or the derived base is
 *  non-positive (e.g. tax ≥ total, which is bad data). */
export function invoiceEffectiveRate(
  taxShown: string | number | null | undefined,
  invoiceTotal: string | number | null | undefined,
): number | null {
  const tax = toNumber(taxShown);
  const total = toNumber(invoiceTotal);
  if (tax === null || total === null) return null;
  const net = total - tax;
  if (net <= 0) return null;
  return tax / net;
}

/** The effective rate as a one-decimal percentage (e.g. "11.5%"), or an
 *  empty string when it can't be computed. */
export function formatInvoiceEffectiveRate(
  taxShown: string | number | null | undefined,
  invoiceTotal: string | number | null | undefined,
): string {
  const rate = invoiceEffectiveRate(taxShown, invoiceTotal);
  return rate === null ? '' : `${(rate * 100).toFixed(1)}%`;
}
